import { Ban, CheckCircle, Eye, Layers } from 'lucide-react';
import type { AdminTenantListDto } from '../types/tenants.types';
import { getTenantStatusInfo } from '../utils/tenant-status.utils';
import Protected from '@/shared/components/ahkinia/Protected/Protected';
import { Button } from '@/shared/components/ui/button';
import type { DataTableColumn, PaginationMeta } from '@/shared/components/ui/data-table';
import { DataTable } from '@/shared/components/ui/data-table';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import { PERMISSIONS } from '@/shared/constants/permissions';
import { formatIsoDate } from '@/shared/utils/format-date.utils';

interface TenantsTableProps {
  data: AdminTenantListDto[];
  pagination?: PaginationMeta;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onViewDetail?: (tenant: AdminTenantListDto) => void;
  onToggleStatus?: (tenant: AdminTenantListDto) => void;
  onAssignPlan?: (tenant: AdminTenantListDto) => void;
}

export function TenantsTable({
  data,
  pagination,
  onPageChange,
  onPageSizeChange,
  onViewDetail,
  onToggleStatus,
  onAssignPlan,
}: TenantsTableProps) {
  const columns: DataTableColumn<AdminTenantListDto>[] = [
    {
      key: 'name',
      header: 'Espacio de trabajo',
      render: (row) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-light text-sm font-medium">{row.name}</span>
        </div>
      ),
    },
    {
      key: 'ownerId',
      header: 'Propietario',
      render: (row) => <span className="text-primary text-sm">{row.ownerName ?? '—'}</span>,
    },
    {
      key: 'countryCode',
      header: 'País base',
      render: (row) => <span className="text-primary text-sm">{row.countryCode ?? '—'}</span>,
    },
    {
      key: 'maxUsers',
      header: 'Plan activo',
      render: (row) => <span className="text-primary text-sm">{row.maxUsers} usuarios</span>,
    },
    {
      key: 'status',
      header: 'Estado',
      render: (row) => {
        const { variant, label } = getTenantStatusInfo(row.status);
        return <StatusBadge status={variant} label={label} />;
      },
    },
    {
      key: 'createdAt',
      header: 'Creado',
      render: (row) => <span className="text-primary text-sm">{formatIsoDate(row.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Protected permissions={PERMISSIONS.TENANTS_VIEW}>
            <Button
              iconImg={<Eye size={24} />}
              title="Ver detalle"
              onClick={() => onViewDetail?.(row)}
            />
          </Protected>
          <Protected permissions={PERMISSIONS.TENANTS_TOGGLE_STATUS}>
            {row.status === 'Suspended' ? (
              <Button
                iconImg={<CheckCircle size={24} />}
                title="Reactivar"
                onClick={() => onToggleStatus?.(row)}
              />
            ) : (
              <Button
                iconImg={<Ban size={24} />}
                title="Suspender"
                onClick={() => onToggleStatus?.(row)}
              />
            )}
          </Protected>
          <Protected permissions={PERMISSIONS.PLANS_ASSIGN_SPECIAL}>
            <Button
              iconImg={<Layers size={24} />}
              title="Asignar plan"
              onClick={() => onAssignPlan?.(row)}
            />
          </Protected>
        </div>
      ),
    },
  ];

  return (
    <div data-testid="tenants-table">
      <DataTable
        title="Espacios de trabajo"
        subtitle={String(pagination?.totalCount ?? data.length)}
        rows={data}
        columns={columns}
        getRowKey={(row) => row.id}
        pagination={pagination}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}
